import { useEffect, useId, useMemo, useState, type ReactNode } from "react";
import { fmtAgo, splitSymbol } from "@poly/core";
import { useApp } from "../lib/store";
import { useNow } from "../lib/hooks";
import { useDialogFocus } from "../lib/dialogFocus";
import {
  copyButtonPresentation,
  copyTextToClipboard,
  type CopyStatus,
} from "../lib/clipboard";
import {
  getTokenBrandSpec,
  getTokenPackshotUrl,
  type TokenViewAngle,
} from "../brand";
import {
  IconAlert,
  IconCheck,
  IconCopy,
  IconInfo,
  IconPause,
  IconPlay,
  IconRefresh,
} from "./icons";

/* ---------- Page Header ---------- */

export function PageHeader({
  title,
  subtitle,
  badge,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  badge?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="page-header">
      <div className="page-header-info">
        {badge && <div className="page-eyebrow">{badge}</div>}
        <h1>{title}</h1>
        {subtitle && <p className="page-header-desc">{subtitle}</p>}
      </div>
      {actions && <div className="page-header-actions">{actions}</div>}
    </div>
  );
}

/* ---------- High-Density Terminal Metric Strip ---------- */

export function MetricStrip({ children }: { children: ReactNode }) {
  return <div className="metric-strip">{children}</div>;
}

export function MetricCell({
  label,
  value,
  sub,
  trend,
  tooltip,
}: {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  trend?: "up" | "down" | "flat" | "warn";
  tooltip?: string;
}) {
  return (
    <div className="metric-cell" title={tooltip}>
      <div className="metric-label">{label}</div>
      <div className={`metric-value ${trend ?? ""}`}>{value}</div>
      {sub !== undefined && sub !== null && <div className="metric-sub">{sub}</div>}
    </div>
  );
}

/* ---------- Cards & Panels ---------- */

export function Card({
  title,
  subtitle,
  children,
  actions,
  flush,
  footer,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  actions?: ReactNode;
  flush?: boolean;
  footer?: ReactNode;
}) {
  return (
    <section className={`panel ${flush ? "panel-flush" : ""}`}>
      {(title || actions || subtitle) && (
        <div className="panel-header">
          <div className="panel-title-wrap">
            {title && <h2 className="panel-title">{title}</h2>}
            {subtitle && <span className="panel-subtitle">{subtitle}</span>}
          </div>
          {actions && <div className="row-flex">{actions}</div>}
        </div>
      )}
      {children}
      {footer && <div className="panel-footer">{footer}</div>}
    </section>
  );
}

export function Stat({
  label,
  value,
  sub,
  trend,
}: {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  trend?: "up" | "down" | "flat" | "warn";
}) {
  return (
    <div className="card stat">
      <div className="label">{label}</div>
      <div className={`value ${trend ?? ""}`}>{value}</div>
      {sub && <div className="sub">{sub}</div>}
    </div>
  );
}

export function Pill({
  kind,
  children,
  title,
  liveDot,
}: {
  kind?: "up" | "down" | "accent" | "warn" | "info";
  children: ReactNode;
  title?: string;
  liveDot?: boolean;
}) {
  return (
    <span className={`pill ${kind ?? ""}`} title={title}>
      {liveDot && <span className="dot live" />}
      {children}
    </span>
  );
}

/* ---------- Data Freshness & Poll Controls ---------- */

export function DataFreshness({
  updatedAt,
  refreshing,
  error,
  intervalSec,
  paused,
  onTogglePause,
  onRefresh,
}: {
  updatedAt: number | null;
  refreshing?: boolean;
  error?: string | null;
  intervalSec?: number;
  paused?: boolean;
  onTogglePause?: () => void;
  onRefresh?: () => void;
}) {
  const now = useNow(1000);
  const ageMs = updatedAt ? Math.max(0, now - updatedAt) : null;
  const staleThresholdMs = (intervalSec ?? 5) * 3500;
  const isStale = ageMs !== null && ageMs > staleThresholdMs;

  return (
    <div className="row-flex" style={{ gap: 6 }}>
      <span
        className={`pill ${
          error
            ? "warn"
            : paused
              ? ""
              : updatedAt
                ? isStale
                  ? "warn"
                  : "up"
                : ""
        }`}
        title={
          updatedAt
            ? `Last successful update: ${new Date(updatedAt).toISOString()}${
                intervalSec ? ` · Polling every ${intervalSec}s` : ""
              }`
            : "Waiting for initial response"
        }
      >
        <span className={`dot ${!paused && !error && updatedAt ? "live" : ""}`} />
        {paused
          ? "Paused"
          : refreshing && !updatedAt
            ? "Loading…"
            : updatedAt
              ? `Updated ${fmtAgo(updatedAt, now)}`
              : "Waiting for data"}
      </span>
      {onTogglePause && (
        <button
          type="button"
          className="btn sm ghost icon-btn"
          onClick={onTogglePause}
          title={paused ? "Resume live polling" : "Pause live polling"}
          aria-label={paused ? "Resume live polling" : "Pause live polling"}
        >
          {paused ? <IconPlay size={13} /> : <IconPause size={13} />}
        </button>
      )}
      {onRefresh && (
        <button
          type="button"
          className="btn sm ghost"
          onClick={onRefresh}
          disabled={refreshing}
          title="Refresh data"
          aria-label="Refresh data"
        >
          <IconRefresh size={13} />
          <span>{refreshing ? "Loading" : "Refresh"}</span>
        </button>
      )}
    </div>
  );
}

/* ---------- Error, Empty, Loading States ---------- */

export type ErrorBoxKind = "unavailable" | "invalid" | "notfound" | "tool";

export function ErrorBox({
  error,
  kind = "unavailable",
  onRetry,
  hasStaleData,
}: {
  error: string | null;
  kind?: ErrorBoxKind;
  onRetry?: () => void;
  hasStaleData?: boolean;
}) {
  if (!error) return null;

  const isInvalid =
    kind === "invalid" ||
    /^(invalid_argument|validation error|invalid )/i.test(error);

  const heading = isInvalid
    ? "That ID does not look right."
    : kind === "notfound"
      ? "No public record found."
      : kind === "tool"
        ? "Something went wrong."
        : "Cannot reach the market data service.";

  const helper = isInvalid
    ? "A transaction hash starts with 0x followed by 64 hex characters. A flow ID looks like 123e4567-…. Check the ID and try again."
    : kind === "notfound"
      ? "Polyester has no public record for this ID. That does not mean the transfer failed."
      : hasStaleData
        ? "Showing the last data we received. Retrying automatically."
        : "Retrying automatically until the service responds.";

  return (
    <div
      className={`notice ${isInvalid || kind === "notfound" ? "warn" : "error"}`}
      role="status"
      aria-live="polite"
    >
      <div className="row-flex" style={{ gap: 10 }}>
        <IconAlert
          size={16}
          style={{
            color: isInvalid || kind === "notfound" ? "var(--warn)" : "var(--down)",
            flexShrink: 0,
          }}
        />
        <div>
          <div>
            <strong>{heading}</strong> {error}{" "}
          </div>
          <div className="faint" style={{ marginTop: 2 }}>
            {helper}
          </div>
        </div>
      </div>
      {onRetry && (
        <button type="button" className="btn sm" onClick={onRetry}>
          <IconRefresh size={13} /> Try again
        </button>
      )}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
  icon,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <div className="empty-state-icon">{icon ?? <IconInfo size={18} />}</div>
      <h4>{title}</h4>
      {description && <p>{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export function Loading({ lines = 5 }: { lines?: number }) {
  return (
    <div
      className="grid"
      style={{ gap: 10, padding: "8px 0" }}
      aria-busy="true"
      aria-label="Loading data"
    >
      {Array.from({ length: lines }).map((_, i) => (
        <div className="skeleton" key={i} style={{ width: `${94 - (i % 5) * 8}%` }} />
      ))}
    </div>
  );
}

/* ---------- Accessible Technical Term Tooltip (A11Y-02) ---------- */

export function TermTip({ label, tip }: { label: ReactNode; tip: string }) {
  const id = useId();
  return (
    <button
      type="button"
      className="term-tip"
      title={tip}
      aria-describedby={id}
    >
      <span>{label}</span>
      <span id={id} role="tooltip" className="term-tip-pop">
        {tip}
      </span>
    </button>
  );
}

/* ---------- Copy Button ---------- */

export function CopyButton({
  text,
  label = "Copy",
  copiedLabel = "Copied",
  size = "sm",
  disabled = false,
}: {
  text: string;
  label?: string;
  copiedLabel?: string;
  size?: "sm" | "md";
  disabled?: boolean;
}) {
  const [status, setStatus] = useState<CopyStatus>("idle");

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const succeeded = await copyTextToClipboard(text, (value) =>
      navigator.clipboard.writeText(value),
    );
    setStatus(succeeded ? "copied" : "failed");
    setTimeout(() => setStatus("idle"), succeeded ? 1600 : 2400);
  };

  const presentation = copyButtonPresentation(status, label, copiedLabel);

  return (
    <button
      type="button"
      className={`btn ${size === "sm" ? "sm" : ""} ${status === "copied" ? "primary" : "ghost"}`}
      onClick={(e) => void handleCopy(e)}
      disabled={disabled}
      aria-label={disabled ? `${label}: nothing to copy yet` : presentation.accessibleLabel}
      title={disabled ? `${label}: nothing to copy yet` : presentation.title}
    >
      {status === "copied" ? <IconCheck size={12} /> : status === "failed" ? <IconAlert size={12} /> : <IconCopy size={12} />}
      <span aria-live="polite">{presentation.visibleLabel}</span>
    </button>
  );
}

/* ---------- Accessible Confirmation Modal ---------- */

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  danger = true,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  // Every dialog instance owns its own id, so two confirmations can never
  // collide on a hard-coded label reference.
  const titleId = useId();
  const dialogRef = useDialogFocus<HTMLDivElement>(open, onCancel);

  if (!open) return null;

  return (
    <div className="modal-backdrop" onClick={onCancel} role="presentation">
      <div
        ref={dialogRef}
        className="modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id={titleId}>{title}</h3>
        <div>{typeof message === "string" ? <p>{message}</p> : message}</div>
        <div className="modal-actions">
          <button type="button" className="btn ghost" onClick={onCancel} data-autofocus>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`btn ${danger ? "danger" : "primary"}`}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Symbol Visuals & Selector ---------- */

const PALETTE = [
  "#5e6ad2",
  "#0ea5e9",
  "#d97706",
  "#10b981",
  "#e11d48",
  "#7c3aed",
  "#0d9488",
  "#ea580c",
  "#2563eb",
  "#9333ea",
];

export function assetColor(asset: string): string {
  const spec = getTokenBrandSpec(asset);
  if (spec) return spec.color;
  let h = 0;
  for (let i = 0; i < asset.length; i++) h = (h * 31 + asset.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
}

export function SymBadge({
  asset,
  size = 22,
  angle = "front",
}: {
  asset: string;
  size?: number;
  angle?: TokenViewAngle;
}) {
  const spec = getTokenBrandSpec(asset);
  const packshotUrl = getTokenPackshotUrl(asset, angle);
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [asset, angle]);

  return (
    <span
      className="sym-badge"
      style={{
        width: size,
        height: size,
        flex: `0 0 ${size}px`,
        background: spec ? spec.bgSoft : undefined,
        borderColor: spec ? `${spec.color}40` : undefined,
      }}
      title={spec ? `${spec.name} (${spec.symbol})` : asset}
      aria-hidden="true"
    >
      {packshotUrl && !imgFailed ? (
        <img
          src={packshotUrl}
          alt={spec?.name ?? asset}
          className="sym-badge-img"
          loading="lazy"
          onError={() => setImgFailed(true)}
        />
      ) : (
        <span
          className="sym-badge-glyph"
          style={{ color: spec?.color ?? "var(--text-secondary)" }}
        >
          {spec ? spec.glyph : asset.slice(0, 3)}
        </span>
      )}
    </span>
  );
}

export function PairLogo({
  base,
  quote,
  size = 22,
}: {
  base: string;
  quote: string;
  size?: number;
}) {
  const quoteSize = Math.max(14, Math.round(size * 0.68));
  return (
    <span className="pair-logo" aria-hidden="true">
      <SymBadge asset={base} size={size} />
      <span className="pair-logo-quote">
        <SymBadge asset={quote} size={quoteSize} />
      </span>
    </span>
  );
}

const FALLBACK_SYMBOLS = [
  "BTC-USDT",
  "ETH-USDT",
  "SOL-USDT",
  "HYPE-USDT",
  "XRP-USDT",
  "BNB-USDT",
  "DOGE-USDT",
  "AVAX-USDT",
  "LTC-USDT",
  "BCH-USDT",
  "POL-USDT",
  "TRX-USDT",
  "ETC-USDT",
  "USDC-USDT",
  "ETH-USDC",
  "SOL-USDC",
  "ETH-BTC",
  "XRP-BTC",
];

export function SymbolSelect({
  value,
  onChange,
  ariaLabel = "Select market pair",
}: {
  value: string;
  onChange: (s: string) => void;
  ariaLabel?: string;
}) {
  const { symbols, pairBySymbol } = useApp();
  const options = symbols.length ? symbols : FALLBACK_SYMBOLS;
  const pair = pairBySymbol.get(value);
  const [base] = splitSymbol(value);

  return (
    <div className="row-flex" style={{ gap: 6, flexWrap: "nowrap" }}>
      <SymBadge asset={base} />
      <select
        className="select mono"
        style={{ width: "auto", fontWeight: 600, paddingRight: 28 }}
        value={value}
        aria-label={ariaLabel}
        title={
          pair
            ? `Tick size: ${pair.tickSize} · Step size: ${pair.stepSize} · Min notional: ${pair.minNotionalQuote} ${pair.quoteAsset}`
            : value
        }
        onChange={(e) => onChange(e.target.value)}
      >
        {!options.includes(value) && <option value={value}>{value}</option>}
        {options.map((s) => {
          const p = pairBySymbol.get(s);
          const disabled = p && p.status !== "enabled";
          return (
            <option key={s} value={s}>
              {s}
              {disabled ? ` (${p.status})` : ""}
            </option>
          );
        })}
      </select>
    </div>
  );
}

/* ---------- Sparkline (Dependency-free SVG) ---------- */

export function Sparkline({
  data,
  width = 110,
  height = 28,
  stroke = "var(--accent)",
}: {
  data: number[];
  width?: number;
  height?: number;
  stroke?: string;
}) {
  const geom = useMemo(() => {
    if (data.length < 2) return null;
    const min = Math.min(...data);
    const max = Math.max(...data);
    const span = max - min || 1;
    const stepX = width / (data.length - 1);
    const pts = data.map((v, i) => ({
      x: i * stepX,
      y: height - 4 - ((v - min) / span) * (height - 8),
    }));
    const line = pts
      .map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`)
      .join(" ");
    const area = `${line} L${width.toFixed(1)},${height} L0,${height} Z`;
    const up = data[data.length - 1] >= data[0];
    const last = pts[pts.length - 1];
    return { line, area, up, last };
  }, [data, width, height]);

  if (!geom) {
    return (
      <span className="faint mono" style={{ fontSize: 11 }} title="Collecting session ticks">
        1 tick
      </span>
    );
  }

  const color = stroke === "var(--accent)" ? (geom.up ? "var(--up)" : "var(--down)") : stroke;
  const fill =
    stroke === "var(--accent)"
      ? geom.up
        ? "var(--up-soft)"
        : "var(--down-soft)"
      : "var(--accent-soft)";

  return (
    <svg width={width} height={height} aria-hidden="true" style={{ display: "inline-block", verticalAlign: "middle" }}>
      <path d={geom.area} fill={fill} />
      <path d={geom.line} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
      <circle cx={geom.last.x} cy={geom.last.y} r="2" fill={color} />
    </svg>
  );
}

/* ---------- Interactive JSON Inspector (Lifecycle Payloads) ---------- */

export function JsonView({ value, depth = 0 }: { value: unknown; depth?: number }) {
  if (value === null || value === undefined) return <span className="faint mono">null</span>;
  if (typeof value === "boolean") {
    return <span className="mono" style={{ color: "var(--warn)" }}>{String(value)}</span>;
  }
  if (typeof value === "number") {
    return <span className="mono" style={{ color: "var(--info)" }}>{String(value)}</span>;
  }
  if (typeof value === "string") {
    const isLongId = value.length >= 24 && !value.includes(" ");
    return (
      <span className="row-flex" style={{ justifyContent: "flex-end", gap: 6, display: "inline-flex" }}>
        <span className="mono" style={{ color: depth === 0 ? "var(--text-primary)" : "var(--up)" }}>
          {value}
        </span>
        {isLongId && <CopyButton text={value} label="Copy" />}
      </span>
    );
  }
  if (Array.isArray(value)) {
    if (!value.length) return <span className="faint mono">[]</span>;
    return (
      <div style={{ marginLeft: depth ? 12 : 0 }}>
        {value.map((v, i) => (
          <div key={i} className="kv">
            <span className="k mono">[{i}]</span>
            <span className="v">
              <JsonView value={v} depth={depth + 1} />
            </span>
          </div>
        ))}
      </div>
    );
  }
  const entries = Object.entries(value as Record<string, unknown>);
  if (!entries.length) return <span className="faint mono">{"{}"}</span>;
  return (
    <div style={{ marginLeft: depth ? 12 : 0 }}>
      {entries.map(([k, v]) => (
        <div key={k} className="kv">
          <span className="k mono">{k}</span>
          <span className="v" style={{ maxWidth: "74%" }}>
            <JsonView value={v} depth={depth + 1} />
          </span>
        </div>
      ))}
    </div>
  );
}

/* ---------- Countdown (Testnet Daily Claim 00:00 UTC) ---------- */

export function Countdown({ to, label }: { to: number; label: string }) {
  const now = useNow(1000);
  const dayMs = 86_400_000;
  const effectiveTo =
    to > now
      ? to
      : Date.UTC(
          new Date(now).getUTCFullYear(),
          new Date(now).getUTCMonth(),
          new Date(now).getUTCDate() + 1,
          0,
          0,
          0,
          0,
        );
  const ms = Math.max(0, effectiveTo - now);
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  const s = Math.floor((ms % 60_000) / 1000);
  const pad = (n: number) => String(n).padStart(2, "0");

  // Progress through current 24h UTC window
  const elapsedPct = Math.min(100, Math.max(0, ((dayMs - ms) / dayMs) * 100));

  return (
    <div>
      <div className="faint mb-2">{label}</div>
      <div
        className="mono"
        style={{
          fontSize: 32,
          fontWeight: 700,
          letterSpacing: 1.5,
          color: "var(--text-primary)",
        }}
        role="timer"
        aria-live="off"
        aria-label="Time remaining until the next daily claim reset"
      >
        {pad(h)}h : {pad(m)}m : {pad(s)}s
      </div>
      <div className="range mt-2" style={{ width: "100%", height: 6 }}>
        <div
          className="fill"
          style={{
            width: `${elapsedPct.toFixed(1)}%`,
            background: "var(--accent)",
          }}
        />
      </div>
      <div className="between faint mt-1">
        <span>00:00 UTC start</span>
        <span>{elapsedPct.toFixed(0)}% of UTC day elapsed</span>
        <span>00:00 UTC reset</span>
      </div>
    </div>
  );
}
